import { createFileRoute, Link } from "@tanstack/react-router";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { useQuery } from "@tanstack/react-query";
import { formatBytes } from "@alm/domain";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { RollupByBuilding } from "./-components/rollup-by-building";
import { RollupLibraries } from "./-components/rollup-libraries";
import { EmptyRows } from "@/components/app/lists/empty-rows";

/** A report ABOUT the drives: how much is filed, where, and how far it reaches, including "shared
 * out of private folders". No personal-drive file is named, enforced by the API. */
export const Route = createFileRoute("/_authenticated/_knowledge/knowledgebase/rollup")({
  beforeLoad: ({ context }) => guardRoute(context.me, "knowledgebase-rollup"),
  component: Rollup,
});

function Rollup() {
  const { facilityId } = useFacility();
  const report = useQuery(orpc.knowledge.files.rollup.queryOptions({ input: { facilityId } }));
  /* Narrowed once here rather than with `report.data?.` at forty call sites: an
     optional chain per cell would render a table of dashes on the one render
     where the query has not settled, instead of the skeleton below. */
  const data = report.data;

  return (
    <>
      <PageHeader
        title="Knowledgebase roll-up"
        meta="What is filed across the organization, where it sits, and how widely it is shared."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/knowledgebase">Back to the library</Link>
          </Button>
        }
      />

      {report.isLoading ? (
        <div className="mt-6 space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : report.isError ? (
        <ReadFailed
          className="mt-6"
          message="The roll-up could not be read."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : !data ? null : (
        <div className="mt-6 space-y-6">
          <StatTiles>
            <StatTile label="Libraries" value={String(data.totals.drives)} />
            <StatTile label="Folders" value={String(data.totals.folders)} />
            <StatTile label="Files" value={String(data.totals.files)} />
            <StatTile label="Stored" value={formatBytes(data.totals.bytes)} />
          </StatTiles>

          <section aria-labelledby="sharing-reach" className="space-y-2">
            <h2 id="sharing-reach" className="text-lg font-semibold tracking-[-0.015em]">
              Sharing reach
            </h2>
            <StatTiles>
              <StatTile label="Shared org-wide" value={String(data.exposure.toOrganization)} />
              <StatTile label="Shared with a building" value={String(data.exposure.toFacility)} />
              <StatTile label="Shared with a person" value={String(data.exposure.toPerson)} />
              <StatTile
                label="Out of personal folders"
                value={String(data.exposure.fromPersonalDrives)}
                hint="Filing that has left its author without being filed anywhere shared."
              />
              <StatTile label="Expired shares" value={String(data.exposure.expired)} />
            </StatTiles>
          </section>

          <RollupByBuilding rows={data.byFacility} />

          <RollupLibraries rows={data.drives} />

          <Card>
            <CardHeader>
              <CardTitle>Recent changes in the shared libraries</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3 text-sm">
                Personal folders are counted above but never named here.
              </p>
              {data.recentInSharedLibraries.length === 0 ? (
                <EmptyRows text="Nothing yet." />
              ) : (
                <ul className="divide-y rounded-md border">
                  {data.recentInSharedLibraries.map((row) => (
                    <li key={row.fileId} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="min-w-0 flex-1 truncate">{row.name}</span>
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {row.updatedByName ?? "Someone"}
                      </span>
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {new Date(row.updatedAt).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
